import type { SdkPurpose, SdkWidgetSettings, SdkPolicyVersion, ConsentStatus } from '../types';
import { el, on } from './dom-utils';
import { createVeridionBranding } from './branding';
import { renderPolicyContentToHtml } from './policy-content';

export interface ModalCallbacks {
  onSave: (decisions: Map<string, boolean>) => Promise<void>;
  onAcceptAll: () => Promise<void>;
  onRejectAll: () => Promise<void>;
  onClose: () => void;
}

function isFullPolicyUrl(url: string): boolean {
  return /^https?:\/\//i.test(url.trim());
}

/**
 * Renders the preferences modal and returns the root overlay element.
 * If policyVersions is provided and privacyPolicyUrl is not a full URL, a "View privacy policy" button
 * opens an in-modal browser of policy versions (most recent first, like a history).
 */
export function createModal(
  settings: SdkWidgetSettings,
  purposes: SdkPurpose[],
  currentConsents: Map<string, ConsentStatus>,
  callbacks: ModalCallbacks,
  policyVersions: SdkPolicyVersion[] = [],
): HTMLElement {
  const toggleState = new Map<string, boolean>();

  // Initialise toggle state from existing consents
  for (const purpose of purposes) {
    const status = currentConsents.get(purpose.id);
    toggleState.set(purpose.id, status === 'granted');
  }

  // ── Header (powered by at top, then title + close) ──
  const showClose = settings.modalShowCloseButton !== false;
  const closeBtn = el('button', { className: showClose ? 'vrd-modal__close' : 'vrd-modal__close vrd-modal__close--hidden', 'aria-label': 'Close' });
  closeBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
  const header = el('div', { className: 'vrd-modal__header' });
  const headerRow = el('div', { className: 'vrd-modal__header-row' });
  headerRow.appendChild(el('h2', { className: 'vrd-modal__title', textContent: settings.companyName || 'Cookie Preferences' }));
  const headerRight = el('div', { className: 'vrd-modal__header-right' });
  headerRight.appendChild(createVeridionBranding(settings.theme));
  headerRight.appendChild(closeBtn);
  headerRow.appendChild(headerRight);
  header.appendChild(headerRow);

  // ── Body: policy link/button + purpose rows ──
  const body = el('div', { className: 'vrd-modal__body' });
  const policySummary = el('div', { className: 'vrd-policy-summary' });

  const policyUrl = settings.privacyPolicyUrl?.trim();
  const hasPolicyLink = !!policyUrl;
  const isExternalPolicy = policyUrl ? isFullPolicyUrl(policyUrl) : false;
  const hasPolicyVersions = policyVersions.length > 0;
  const sortedVersions = [...policyVersions].sort((a, b) => b.versionNumber - a.versionNumber);

  if (hasPolicyLink) {
    const link = el('a', {
      className: 'vrd-policy-link',
      href: policyUrl!,
      target: isExternalPolicy ? '_blank' : '_self',
      ...(isExternalPolicy ? { rel: 'noopener noreferrer' as const } : {}),
      textContent: 'Read our Privacy Policy',
    });
    policySummary.appendChild(link);
    body.appendChild(policySummary);
  } else if (hasPolicyVersions) {
    const viewPolicyBtn = el('button', {
      type: 'button',
      className: 'vrd-btn vrd-btn--link vrd-policy-view-btn',
      textContent: 'View privacy policy',
    });
    policySummary.appendChild(viewPolicyBtn);
    body.appendChild(policySummary);

    // ── Policy viewer panel (overlay inside modal body) ──
    const policyViewer = el('div', { className: 'vrd-policy-viewer', 'aria-hidden': 'true' });
    const policyViewerHeader = el('div', { className: 'vrd-policy-viewer__header' });
    const policyViewerBack = el('button', { type: 'button', className: 'vrd-policy-viewer__back', textContent: '← Back' });
    policyViewerHeader.appendChild(el('h3', { className: 'vrd-policy-viewer__title', textContent: 'Privacy policy' }));
    policyViewerHeader.appendChild(policyViewerBack);

    const versionLabel = el('label', { className: 'vrd-policy-viewer__version-label', textContent: 'Version: ' });
    const versionSelect = document.createElement('select');
    versionSelect.className = 'vrd-policy-viewer__select';
    versionSelect.setAttribute('aria-label', 'Policy version');
    sortedVersions.forEach((pv, idx) => {
      const opt = document.createElement('option');
      opt.value = String(idx);
      opt.textContent = idx === 0 ? `Latest (v${pv.versionNumber})` : `v${pv.versionNumber} – ${pv.policyName}`;
      versionSelect.appendChild(opt);
    });

    const versionRow = el('div', { className: 'vrd-policy-viewer__version-row' });
    versionRow.appendChild(versionLabel);
    versionRow.appendChild(versionSelect);

    const contentBox = el('div', { className: 'vrd-policy-viewer__content' });
    contentBox.setAttribute('role', 'article');

    function renderPolicyContent(): void {
      const idx = Number(versionSelect.value) || 0;
      const pv = sortedVersions[idx];
      if (pv) {
        contentBox.innerHTML = renderPolicyContentToHtml(pv.content);
      }
    }

    on(versionSelect, 'change', renderPolicyContent);
    renderPolicyContent();

    policyViewer.appendChild(policyViewerHeader);
    policyViewer.appendChild(versionRow);
    policyViewer.appendChild(contentBox);
    body.appendChild(policyViewer);

    on(viewPolicyBtn, 'click', () => {
      policyViewer.classList.add('vrd-policy-viewer--visible');
      policyViewer.setAttribute('aria-hidden', 'false');
    });
    on(policyViewerBack, 'click', () => {
      policyViewer.classList.remove('vrd-policy-viewer--visible');
      policyViewer.setAttribute('aria-hidden', 'true');
    });
  }

  // Normalize to string so API type quirks (e.g. number vs string id) don't break grouping
  const idOf = (p: SdkPurpose) => String(p.id);
  const parentIdOf = (p: SdkPurpose) =>
    p.parentPurposeId != null && p.parentPurposeId !== '' ? String(p.parentPurposeId) : null;

  const purposeIds = new Set(purposes.map(idOf));
  const roots = purposes.filter((p) => !parentIdOf(p));
  const childrenByParent = new Map<string, SdkPurpose[]>();
  for (const p of purposes) {
    const parentId = parentIdOf(p);
    if (parentId) {
      const list = childrenByParent.get(parentId) ?? [];
      list.push(p);
      childrenByParent.set(parentId, list);
    }
  }

  function addPurposeRow(container: HTMLElement, purpose: SdkPurpose, isChild: boolean): void {
    const checked = toggleState.get(purpose.id) ? 'checked' : '';
    const row = el('div', { className: isChild ? 'vrd-purpose vrd-purpose--child' : 'vrd-purpose' });

    const input = document.createElement('input');
    input.type = 'checkbox';
    input.id = `vrd-toggle-${purpose.id}`;
    if (checked) input.checked = true;

    on(input, 'change', () => {
      toggleState.set(purpose.id, input.checked);
    });

    const track = el('label', { className: 'vrd-toggle__track' });
    track.setAttribute('for', input.id);

    const toggle = el('div', { className: 'vrd-toggle' });
    toggle.appendChild(input);
    toggle.appendChild(track);

    const headerRow = el(
      'div',
      { className: 'vrd-purpose__header' },
      el('span', { className: 'vrd-purpose__name', textContent: purpose.name }),
      toggle,
    );

    row.appendChild(headerRow);
    if (purpose.description) {
      row.appendChild(el('p', { className: 'vrd-purpose__desc', textContent: purpose.description }));
    }

    container.appendChild(row);
  }

  for (const root of roots) {
    const rootKey = idOf(root);
    const children = childrenByParent.get(rootKey) ?? [];
    if (children.length > 0) {
      const section = el('div', { className: 'vrd-purpose-group' });
      const content = el('div', { className: 'vrd-purpose-group__content' });
      const chevron = el('span', { className: 'vrd-purpose-group__chevron', textContent: '▼', 'aria-hidden': 'true' });
      const titleEl = el('span', { className: 'vrd-purpose-group__title', textContent: root.name });
      const header = el('div', { className: 'vrd-purpose-group__header' });
      header.appendChild(chevron);
      header.appendChild(titleEl);
      header.setAttribute('role', 'button');
      header.setAttribute('aria-expanded', 'true');
      header.setAttribute('aria-label', `Expand or collapse ${root.name}`);
      on(header, 'click', () => {
        const collapsed = section.classList.toggle('vrd-purpose-group--collapsed');
        header.setAttribute('aria-expanded', String(!collapsed));
      });
      section.appendChild(header);
      for (const child of children) addPurposeRow(content, child, true);
      section.appendChild(content);
      body.appendChild(section);
    } else {
      addPurposeRow(body, root, false);
    }
  }

  // Orphan children: have parentPurposeId but parent not in the purposes list — show as top-level
  const orphanChildren = purposes.filter((p) => {
    const parentId = parentIdOf(p);
    return parentId != null && !purposeIds.has(parentId);
  });
  for (const child of orphanChildren) {
    addPurposeRow(body, child, false);
  }

  // ── Footer ──
  const rejectAllBtn = el('button', { className: 'vrd-btn vrd-btn--secondary' }) as HTMLButtonElement;
  rejectAllBtn.appendChild(document.createTextNode('Reject All'));
  rejectAllBtn.appendChild(el('span', { className: 'vrd-btn__spinner', 'aria-hidden': 'true' }));

  const acceptAllBtn = el('button', { className: 'vrd-btn vrd-btn--secondary' }) as HTMLButtonElement;
  acceptAllBtn.appendChild(document.createTextNode('Accept All'));
  acceptAllBtn.appendChild(el('span', { className: 'vrd-btn__spinner', 'aria-hidden': 'true' }));

  const saveBtn = el('button', { className: 'vrd-btn vrd-btn--primary' }) as HTMLButtonElement;
  saveBtn.appendChild(document.createTextNode('Save Preferences'));
  saveBtn.appendChild(el('span', { className: 'vrd-btn__spinner', 'aria-hidden': 'true' }));

  const footerBtns = [rejectAllBtn, acceptAllBtn, saveBtn];

  function setModalLoading(active: boolean): void {
    for (const btn of footerBtns) btn.disabled = active;
  }

  const footer = el('div', { className: 'vrd-modal__footer' }, rejectAllBtn, acceptAllBtn, saveBtn);

  // ── Modal box ──
  const modal = el('div', { className: 'vrd-modal' }, header, body, footer);
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Cookie preferences');

  // ── Overlay ──
  const overlay = el('div', { className: 'vrd-overlay' });
  overlay.appendChild(modal);

  // ── Events ──
  on(closeBtn, 'click', callbacks.onClose);
  on(rejectAllBtn, 'click', async () => {
    setModalLoading(true);
    rejectAllBtn.classList.add('vrd-btn--loading');
    try { await callbacks.onRejectAll(); } finally {
      setModalLoading(false);
      rejectAllBtn.classList.remove('vrd-btn--loading');
    }
  });
  on(acceptAllBtn, 'click', async () => {
    setModalLoading(true);
    acceptAllBtn.classList.add('vrd-btn--loading');
    try { await callbacks.onAcceptAll(); } finally {
      setModalLoading(false);
      acceptAllBtn.classList.remove('vrd-btn--loading');
    }
  });
  on(saveBtn, 'click', async () => {
    setModalLoading(true);
    saveBtn.classList.add('vrd-btn--loading');
    try { await callbacks.onSave(new Map(toggleState)); } finally {
      setModalLoading(false);
      saveBtn.classList.remove('vrd-btn--loading');
    }
  });

  // Close on backdrop click
  on(overlay, 'click', (e) => {
    if (e.target === overlay) callbacks.onClose();
  });

  // Close on Escape key
  const escHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      callbacks.onClose();
      document.removeEventListener('keydown', escHandler);
    }
  };
  document.addEventListener('keydown', escHandler);

  return overlay;
}
